import { Box, Button, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

export interface DateRangePickerLayoutProps {
  /** Optional title shown above the picker. */
  title?: ReactNode;
  /** Picker content (e.g. DayPicker). Wrapped with shared rdp styling. */
  children: ReactNode;
  /** Optional summary line below the picker (e.g. "Seleccionado: from – to"). */
  summary?: ReactNode;
  onCancel: () => void;
  onApply: () => void;
  /** Disables the Apply button. */
  applyDisabled?: boolean;
  /** When true, Cancel/Apply buttons are not rendered (e.g. when selection applies on click). */
  hideActions?: boolean;
}

/**
 * Shared layout for custom date range dialogs: title, themed calendar area, optional summary, Cancel/Apply buttons.
 * Applies consistent rdp (react-day-picker) styling and button row to both fixed range and until-now pickers.
 */
export function DateRangePickerLayout({
  title,
  children,
  summary,
  onCancel,
  onApply,
  applyDisabled = false,
  hideActions = false,
}: DateRangePickerLayoutProps) {
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;

  return (
    <Box sx={{ p: 2, minWidth: 360 }}>
      {title != null && title !== '' ? (
        <Typography variant="subtitle2" mb={2}>
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{
          '& .rdp-root': {
            '--rdp-today-color': primaryMain,
            '--rdp-accent-color': primaryMain,
          },
          '& .rdp-chevron': {
            fill: theme.palette.secondary.light,
          },
          '& .rdp-today': {
            fontWeight: 500,
          },
          '& .rdp-selected, & .rdp-range_start, & .rdp-range_middle, & .rdp-range_end': {
            fontWeight: 500,
          },
          '& .rdp-selected .rdp-day_button': {
            fontWeight: 500,
            backgroundColor: primaryMain,
            color: theme.palette.primary.contrastText,
          },
        }}
      >
        {children}
      </Box>
      {summary != null ? (
        <Box sx={{ mt: 2, fontSize: '0.875rem', color: 'text.secondary' }}>{summary}</Box>
      ) : null}
      {!hideActions ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button size="small" color="secondary" onClick={onCancel}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
          </Button>
          <Button size="small" variant="contained" onClick={onApply} disabled={applyDisabled}>
            <FormattedMessage id="common.dateRangeSelector.apply" defaultMessage="Aplicar" />
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
